import basicAppConfig from "../constants/basicAppConfig";
import { APP_NAME_TO_DEF, APPLICATIONS_ID, APPLICATIONS_NAME, DOMAINS, LOAD_APP_AS_KEY, LOAD_APP_AS_KEY_INITIAL, } from "../constants/domains";
import { BUILD_MODE, LOAD_AS_APPLICATION_ID, RESET_3RD_PARTIES } from "../constants/envVariables";
import getEnumValue from "./getEnumValue";
export function getAppConfigForDomain(hostname) {
    if (hostname === DOMAINS.SECRETMATE) {
        return APP_NAME_TO_DEF.get(APPLICATIONS_NAME.SECRETMATE);
    }
    for (const [appName, domain] of Object.entries(DOMAINS)) {
        if (domain === hostname) {
            return APP_NAME_TO_DEF.get(APPLICATIONS_NAME[appName]);
        }
    }
    for (const appCfg of APP_NAME_TO_DEF.values()) {
        if (appCfg.hasDomain(hostname)) {
            return appCfg;
        }
    }
    return undefined;
}
function resolveCandidateAppName({ isPixelchat, isSecretMate }) {
    if (isSecretMate) {
        return APPLICATIONS_NAME.SECRETMATE;
    }
    if (isPixelchat) {
        return APPLICATIONS_NAME.PIXELCHAT;
    }
    return APPLICATIONS_NAME.SPICYCHAT;
}
export function getAppConfigForSubdomain(hostname) {
    const lowerHostname = hostname.toLowerCase();
    const lowerBuildMode = (BUILD_MODE ?? "").toLowerCase();
    const isPixelchat = lowerBuildMode.includes(APPLICATIONS_ID.PixelChat) || lowerHostname.includes(APPLICATIONS_ID.PixelChat);
    const isSecretMate = lowerBuildMode.includes(APPLICATIONS_ID.SecretMate) || lowerHostname.includes(APPLICATIONS_ID.SecretMate);
    const candidateAppName = resolveCandidateAppName({ isPixelchat, isSecretMate });
    const candidateDefinition = APP_NAME_TO_DEF.get(candidateAppName);
    if (candidateDefinition && candidateDefinition.isParentDomainFor(hostname)) {
        return candidateDefinition;
    }
    if (lowerHostname.endsWith("secretmate.ai")) {
        return APP_NAME_TO_DEF.get(APPLICATIONS_NAME.SECRETMATE);
    }
    if (lowerHostname.endsWith(DOMAINS.PIXELCHAT)) {
        return APP_NAME_TO_DEF.get(APPLICATIONS_NAME.PIXELCHAT);
    }
    if (lowerHostname.endsWith(DOMAINS.SPICYCHAT)) {
        return APP_NAME_TO_DEF.get(APPLICATIONS_NAME.SPICYCHAT);
    }
    return undefined;
}
export function applyOfflineMode(appCfg) {
    if (!appCfg.testDomains.includes("localhost")) {
        appCfg.testDomains.push("localhost");
        appCfg.testDomains.push("127.0.0.1");
    }
}
export function resetThirdPartySettings(resetCallback) {
    if (typeof window === "undefined") {
        return;
    }
    const resetDirective = new URLSearchParams(window.location.search).get("r3s3t");
    if (RESET_3RD_PARTIES === "true" || resetDirective === "true") {
        resetCallback();
    }
}
function resolveLocalhostAppConfig(hostname) {
    const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1";
    if (!isLocalHost) {
        return undefined;
    }
    const fallbackId = LOAD_AS_APPLICATION_ID ?? basicAppConfig.applicationId;
    const applicationName = getEnumValue(APPLICATIONS_NAME, fallbackId, true);
    if (!applicationName) {
        return undefined;
    }
    return APP_NAME_TO_DEF.get(applicationName);
}
function resolveOverrideAppConfig() {
    const overrideAppName = localStorage.getItem(LOAD_APP_AS_KEY) || localStorage.getItem(LOAD_APP_AS_KEY_INITIAL);
    if (!overrideAppName) {
        return undefined;
    }
    const applicationName = getEnumValue(APPLICATIONS_NAME, overrideAppName, true);
    if (!applicationName) {
        return undefined;
    }
    return APP_NAME_TO_DEF.get(applicationName);
}
/**
 * Returns the application configuration required from the URL hostname.
 * On Native (window undefined) returns an empty object.
 * 1. localStorage override (debug menu / initial flag).
 * 2. exact domain match.
 * 3. known testing subdomains.
 * 4. localhost fallback via LOAD_AS_APPLICATION_ID or basicAppConfig.
 * 5. offline mode injects localhost into testDomains for the resolved app.
 */
export default function getConfigFromUrl(hostname) {
    if (typeof window === "undefined") {
        return {};
    }
    let appConfigToReturn = resolveOverrideAppConfig();
    if (!appConfigToReturn) {
        appConfigToReturn = getAppConfigForDomain(hostname);
    }
    if (!appConfigToReturn) {
        appConfigToReturn = getAppConfigForSubdomain(hostname);
    }
    if (!appConfigToReturn) {
        appConfigToReturn = resolveLocalhostAppConfig(hostname);
    }
    const isOffline = (BUILD_MODE ?? "").toLowerCase().includes("offline");
    if (appConfigToReturn && isOffline) {
        applyOfflineMode(appConfigToReturn);
    }
    if (!appConfigToReturn) {
        throw Error("Unknown domain: " + hostname + ". Unable to determine applicationId.");
    }
    return appConfigToReturn;
}
